import React from 'react';
import { useTranslation } from 'react-i18next';
import { LucideIcon, Inbox, Search, Lock } from 'lucide-react';
import { Button } from '../ui/Button';
import { cn } from 'src/utils/cn';

/**
 * Base empty state component with consistent styling
 */
interface BaseEmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
    icon?: LucideIcon;
  };
  className?: string;
}

const BaseEmptyState: React.FC<BaseEmptyStateProps> = ({
  icon: Icon,
  title,
  description,
  action,
  className,
}) => {
  return (
    <div className={cn('flex flex-col items-center justify-center py-12 px-4 text-center', className)}>
      <div className="mx-auto h-16 w-16 text-gray-400 mb-4 flex items-center justify-center">
        <Icon className="h-full w-full" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600 max-w-md mb-6">{description}</p>
      {action && (
        <Button
          onClick={action.onClick}
          variant="primary"
          leftIcon={action.icon ? <action.icon className="h-4 w-4" /> : undefined}
        >
          {action.label}
        </Button>
      )}
    </div>
  );
};

/**
 * First-time empty state - shown when user has never created any data
 * Follows UX spec: friendly, actionable language, primary CTA, clear explanation
 */
export interface FirstTimeEmptyStateProps {
  title: string;
  description: string;
  action: {
    label: string;
    onClick: () => void;
    icon?: LucideIcon;
  };
  icon?: LucideIcon;
  className?: string;
}

export const FirstTimeEmptyState: React.FC<FirstTimeEmptyStateProps> = ({
  title,
  description,
  action,
  icon: Icon = Inbox,
  className,
}) => {
  return (
    <BaseEmptyState
      icon={Icon}
      title={title}
      description={description}
      action={action}
      className={className}
    />
  );
};

/**
 * Filtered empty state - shown when data exists but filters hide all rows
 * Follows UX spec: explanation that filters are applied, clear reset action
 */
export interface FilteredEmptyStateProps {
  onClearFilters: () => void;
  className?: string;
  title?: string;
  description?: string;
}

export const FilteredEmptyState: React.FC<FilteredEmptyStateProps> = ({
  onClearFilters,
  className,
  title,
  description,
}) => {
  const { t } = useTranslation();
  const defaultTitle = title || t('no-results-match-filters');
  const defaultDescription = description || t('adjust-filters-description');
  return (
    <BaseEmptyState
      icon={Search}
      title={defaultTitle}
      description={defaultDescription}
      action={{
        label: t('clear-all-filters'),
        onClick: onClearFilters,
      }}
      className={className}
    />
  );
};

/**
 * Permission-based empty state - shown when user lacks access
 * Follows UX spec: clear reason, next step (contact admin/request access)
 */
export interface PermissionEmptyStateProps {
  subject: string;
  className?: string;
  title?: string;
  description?: string;
}

export const PermissionEmptyState: React.FC<PermissionEmptyStateProps> = ({
  subject,
  className,
  title,
  description,
}) => {
  const { t } = useTranslation();
  const defaultTitle = title || t('no-access') || `You don't have access`;
  const defaultDescription = description || t('no-access-description', { subject }) || `You don't have permission to view ${subject}. Contact an admin to request access.`;

  return (
    <BaseEmptyState
      icon={Lock}
      title={defaultTitle}
      description={defaultDescription}
      className={className}
    />
  );
};

